import { useEffect } from 'react';
import { createPortal } from 'react-dom';

/*
 * Same color-mix() scoped-style approach used across the v2 auth/subscription pages — Tailwind's
 * opacity modifier doesn't work on our var(--cv2-*)-based custom colors. Matches the same
 * backdrop-blur "glass panel" look already established there (e.g. Login.js's .cv2-login-panel).
 */
const SCOPED_STYLES = `
  .cv2-confirm-panel {
    background-color: color-mix(in srgb, var(--cv2-background) 92%, transparent);
    border-color: color-mix(in srgb, var(--cv2-foreground) 10%, transparent);
  }
  .cv2-confirm-message { color: color-mix(in srgb, var(--cv2-foreground) 50%, transparent); }
  .cv2-confirm-cancel {
    color: color-mix(in srgb, var(--cv2-foreground) 55%, transparent);
    border-color: color-mix(in srgb, var(--cv2-foreground) 15%, transparent);
    transition: color 0.15s ease, border-color 0.15s ease;
  }
  .cv2-confirm-cancel:hover {
    color: var(--cv2-foreground);
    border-color: color-mix(in srgb, var(--cv2-foreground) 30%, transparent);
  }
`;

const ConfirmDialog = ({
    variant = 'dark',
    title = 'Are you sure?',
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    onConfirm,
    onCancel,
}) => {
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onCancel();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onCancel]);

    const isDark = variant === 'dark';

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {isDark && <style>{SCOPED_STYLES}</style>}

            <div
                className="absolute inset-0 bg-black/60"
                onClick={onCancel}
            />

            {isDark ? (
                <div className="cv2-dark cv2-confirm-panel relative w-full max-w-sm mx-4 rounded-lg border backdrop-blur-[28px] p-6">
                    <h2 className="font-aeonik font-normal text-xl text-cv2-foreground normal-case mb-2">
                        {title}
                    </h2>
                    {message && (
                        <p className="cv2-confirm-message font-aeonik text-sm leading-[22px] normal-case mb-6">
                            {message}
                        </p>
                    )}
                    <div className="flex items-center justify-end gap-3">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="cv2-confirm-cancel border rounded-md px-4 h-9 font-spaceMono text-[10px] tracking-[0.8px] uppercase"
                        >
                            {cancelLabel}
                        </button>
                        <button
                            type="button"
                            onClick={onConfirm}
                            className="bg-claraview-secondary hover:brightness-110 transition-all rounded-md px-4 h-9 font-spaceMono text-[10px] tracking-[0.8px] text-claraview-quaternary uppercase"
                        >
                            {confirmLabel}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="relative w-full max-w-sm mx-4 rounded-2xl border border-[#E8E6E3] shadow-sm bg-white p-6">
                    <h2 className="text-xl text-[#2D2D2D] mb-2">{title}</h2>
                    {message && (
                        <p className="text-sm text-[#6B6B6B] mb-6">{message}</p>
                    )}
                    <div className="flex items-center justify-end gap-3">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 text-sm bg-white text-[#38433A] border border-[#E8E6E3] rounded-lg hover:bg-[#FAFAF9] transition-colors"
                        >
                            {cancelLabel}
                        </button>
                        <button
                            type="button"
                            onClick={onConfirm}
                            className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                            {confirmLabel}
                        </button>
                    </div>
                </div>
            )}
        </div>,
        document.body
    );
};

export default ConfirmDialog;
